import { isAxiosError } from 'axios';
import type { TFunction } from 'i18next';

export type ApiErrorPayload = {
  error?: string;
  message?: string;
  details?: Record<string, unknown>;
  upstreamStatus?: number;
  correlationId?: string;
  retryAfterSeconds?: number;
  [key: string]: unknown;
};

export type ParsedApiError = {
  status: number | undefined;
  code: string | undefined;
  message: string | undefined;
  details: Record<string, unknown> | undefined;
  upstreamStatus: number | undefined;
  raw: ApiErrorPayload | undefined;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function parseApiError(error: unknown): ParsedApiError {
  let status: number | undefined;
  let raw: ApiErrorPayload | undefined;

  if (isAxiosError(error)) {
    status = error.response?.status;
    const data: unknown = error.response?.data;
    if (isPlainObject(data)) raw = data;
  } else if (isPlainObject(error)) {
    raw = error;
  }

  const code = typeof raw?.error === 'string' ? raw.error : undefined;
  const message = typeof raw?.message === 'string' ? raw.message : undefined;
  const details = isPlainObject(raw?.details) ? raw.details : undefined;
  const upstreamStatus = typeof raw?.upstreamStatus === 'number' ? raw.upstreamStatus : undefined;

  return { status, code, message, details, upstreamStatus, raw };
}

export function translateKnownKey(t: TFunction, key: string): string | null {
  const translated = t(key);
  return translated && translated !== key ? translated : null;
}

export function getApiErrorMessage(
  error: unknown,
  t: TFunction,
  fallbackKey: string = 'errors.serverError'
): string {
  const { code, status } = parseApiError(error);

  if (code) {
    const translated = translateKnownKey(t, `errors.api.${code}`);
    if (translated) return translated;
  }

  if (status !== undefined) {
    const translated = translateKnownKey(t, `errors.api.http.${status}`);
    if (translated) return translated;
  }

  return t(fallbackKey);
}

export function isRecordingQualityError(error: unknown): boolean {
  const { code, upstreamStatus } = parseApiError(error);
  return code === 'upstream_error' && upstreamStatus === 400;
}
